import React, { useState, useEffect, useRef, useCallback } from "react"
import "./Inventory.css"

/* ─────────────────────────────────────────────────────────────────────────
   INVENTORY ITEMS  —  aligned with real DB fields
   Register collection fields used:
     sessionsBooked[]        → sessions the user booked
     numberOfCheckin         → actual check-ins (sessions attended)
     checkedInByExhibitor[]  → booth visits
   Leaderboard collection gname fields:
     "Quiz"            → quiz completion
     "eventFeedback"   → event feedback
     "networkFeedback" → network feedback
     "Master"          → master achievement
     "Score"           → total score
───────────────────────────────────────────────────────────────────────── */
const INVENTORY_ITEMS = [
  {
    id: "session",
    icon: "🎯",
    label: "Session Scout",
    rarity: "common",
    description: "Awarded for checking into sessions at Converge. Each session brings you closer to mastery.",
    statKey: "checkIns",
    statLabel: "Sessions Checked In",
    flavor: "Knowledge is the sharpest weapon.",
    detail: (reg) => reg?.sessionsBooked?.length > 0
      ? `Booked: ${(reg.sessionsBooked || []).join(", ")}`
      : null,
  },
  {
    id: "exhibitor",
    icon: "🏛️",
    label: "Booth Explorer",
    rarity: "uncommon",
    description: "Granted for visiting demo booths on the Converge expo floor. Explore the future of retail tech.",
    statKey: "boothVisits",
    statLabel: "Booths Visited",
    flavor: "Curiosity unlocks every door.",
    detail: (reg) => reg?.checkedInByExhibitor?.length > 0
      ? `${reg.checkedInByExhibitor.length} booth check-ins recorded`
      : null,
  },
  {
    id: "quiz",
    icon: "⚡",
    label: "Quiz Master",
    gname: "Quiz",
    rarity: "rare",
    description: "Earned by completing the Converge Quiz. The quiz can only be attempted once — a true test of courage.",
    statKey: "quizScore",
    statLabel: "Quiz Points",
    flavor: "Once tested, forever proven.",
  },
  {
    id: "eventFeedback",
    icon: "📜",
    label: "Voice of Converge",
    gname: "eventFeedback",
    rarity: "uncommon",
    description: "Awarded for completing the Event Feedback quest. Your insights shape the future of Converge.",
    statKey: "feedbackScore",
    statLabel: "Points Earned",
    flavor: "Every voice echoes through time.",
  },
  {
    id: "networkFeedback",
    icon: "🌐",
    label: "Network Oracle",
    gname: "networkFeedback",
    rarity: "uncommon",
    description: "Awarded for completing the Networking Feedback quest. You helped map the connections of tomorrow.",
    statKey: "networkScore",
    statLabel: "Points Earned",
    flavor: "Bridges built last forever.",
  },
  {
    id: "master",
    icon: "👑",
    label: "Converge Master",
    gname: "Master",
    rarity: "legendary",
    description: "The pinnacle achievement. Awarded to those who have demonstrated complete mastery across all Converge challenges.",
    statKey: "masterScore",
    statLabel: "Master Points",
    flavor: "Legend speaks your name.",
  },
  {
    id: "score",
    icon: "🔮",
    label: "Score Crystal",
    gname: "Score",
    rarity: "epic",
    description: "Your total leaderboard score crystal — the sum of all your Converge accomplishments.",
    statKey: "totalScore",
    statLabel: "Total Points",
    flavor: "All roads lead here.",
  },
  {
    id: "level100",
    icon: "💎",
    label: "Centurion",
    rarity: "mythic",
    description: "The Centurion card — unlocked only at Level 100. A symbol of absolute dedication and unmatched achievement at Converge.",
    statKey: null,
    statLabel: "Level Required: 100",
    flavor: "They said it couldn't be done.",
    requiresLevel: 100,
    comingSoon: true,
  },
]

const RARITY_CONFIG = {
  common:    { color: "#9ca3af", glow: "rgba(156,163,175,.35)", label: "Common" },
  uncommon:  { color: "#22d36b", glow: "rgba(34,211,107,.35)",  label: "Uncommon" },
  rare:      { color: "#3bbfff", glow: "rgba(59,191,255,.4)",   label: "Rare" },
  epic:      { color: "#a855f7", glow: "rgba(168,85,247,.45)",  label: "Epic" },
  legendary: { color: "#f5b731", glow: "rgba(245,183,49,.5)",   label: "Legendary" },
  mythic:    { color: "#ff3c9e", glow: "rgba(255,60,158,.5)",   label: "Mythic" },
}

/* ─────────────────────────────────────────────────────────────────────────
   buildStats — merges Register doc + leaderboard entry into unified stats
───────────────────────────────────────────────────────────────────────── */
function buildStats(lbEntry, regDoc) {
  const scores  = lbEntry?.scores || []
  const findLb  = gname => scores.find(s => s.gname?.toLowerCase() === gname.toLowerCase())
  const quiz    = findLb("Quiz")
  const ef      = findLb("eventFeedback")
  const nf      = findLb("networkFeedback")
  const master  = findLb("Master")
  const scoreLb = findLb("Score")

  // Register fields
  const checkIns    = regDoc?.numberOfCheckin         ?? 0
  const boothVisits = (regDoc?.checkedInByExhibitor   ?? []).length
  const sessBooked  = (regDoc?.sessionsBooked         ?? []).length

  return {
    // From Register
    checkIns,
    boothVisits,
    sessBooked,
    ticketVariant: regDoc?.Ticket_Variant  || "",
    badgeType:     regDoc?.badgeType       || "",
    // From Leaderboard
    totalScore:   lbEntry?.totalScore    ?? (scoreLb ? Number(scoreLb.score) : 0),
    lbSessions:   lbEntry?.sessionsCount ?? 0,
    lbBooths:     lbEntry?.boothsCount   ?? 0,
    quizScore:    quiz   ? Number(quiz.score)   : null,
    feedbackScore: ef    ? Number(ef.score)     : null,
    networkScore:  nf    ? Number(nf.score)     : null,
    masterScore:   master ? Number(master.score) : null,
  }
}

function isEarned(item, stats) {
  if (item.requiresLevel) return false
  switch (item.statKey) {
    case "checkIns":      return stats.checkIns    > 0
    case "boothVisits":   return stats.boothVisits > 0
    case "totalScore":    return stats.totalScore  > 0
    case "quizScore":     return stats.quizScore   !== null
    case "feedbackScore": return stats.feedbackScore !== null
    case "networkScore":  return stats.networkScore  !== null
    case "masterScore":   return stats.masterScore   !== null
    default: return false
  }
}

function getStatValue(item, stats) {
  if (!item.statKey) return null
  const v = stats[item.statKey]
  return v !== null && v !== undefined ? v : 0
}

/* ─────────────────────────────────────────────────────────────────────────
   ORNAMENTAL CORNERS  — all 4 wrapped in one absolutely-positioned div
   so they do NOT participate in flex layout of parent card
───────────────────────────────────────────────────────────────────────── */
/* ─────────────────────────────────────────────────────────────────────────
   CornerSVG  —  4 corners, each drawn explicitly (no CSS transforms).
   Each SVG has its own viewBox origin so it renders in its own corner.
───────────────────────────────────────────────────────────────────────── */
const CornerSVG = ({ gold = false }) => {
  const c1  = gold ? "#8a6018" : "rgba(59,191,255,.6)"
  const c2  = gold ? "#c9962a" : "rgba(59,191,255,.3)"
  const dot = gold ? "#f5b731" : "#3bbfff"
  const vn  = gold ? "#d4a830" : "rgba(59,191,255,.5)"

  const W = 90, H = 90
  const style = { position:"absolute", display:"block", pointerEvents:"none", overflow:"visible", width:W, height:H }

  /* ── Top-left: arms go RIGHT and DOWN ── */
  const TL = () => (
    <svg style={{...style, top:-5, left:-5}} viewBox="0 0 90 90" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 86 L4 10 Q4 4 10 4 L86 4" fill="none" stroke={c1} strokeWidth="2.5"/>
      <path d="M9 80 L9 14 Q9 9 14 9 L80 9" fill="none" stroke={c2} strokeWidth="1.2"/>
      <circle cx="8" cy="8" r="4.5" fill={dot} stroke={c1} strokeWidth="1.2"/>
      <circle cx="8" cy="8" r="2" fill="#fff8c0" opacity=".7"/>
      <path d="M16 4 Q22 10 16 17 Q23 13 30 17 Q25 10 30 4 Q23 8 16 4Z" fill={c1}/>
      <path d="M4 16 Q10 22 17 16 Q13 23 17 30 Q10 25 4 30 Q8 23 4 16Z" fill={c1}/>
      <circle cx="16" cy="16" r="5" fill={c1} stroke={vn} strokeWidth="1.2"/>
      <circle cx="16" cy="16" r="2.5" fill={dot}/>
      <circle cx="26" cy="8" r="3.2" fill={vn}/>
      <circle cx="21" cy="5" r="2" fill={dot} opacity=".85"/>
      <circle cx="8" cy="26" r="3.2" fill={vn}/>
      <circle cx="5" cy="21" r="2" fill={dot} opacity=".85"/>
      <path d="M8 38 Q11 30 16 22" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <path d="M38 8 Q30 11 22 16" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <circle cx="10" cy="40" r="2" fill={dot} opacity=".5"/>
      <circle cx="40" cy="10" r="2" fill={dot} opacity=".5"/>
    </svg>
  )

  /* ── Top-right: arms go LEFT and DOWN — mirrored on X axis ── */
  const TR = () => (
    <svg style={{...style, top:-5, right:-5}} viewBox="0 0 90 90" xmlns="http://www.w3.org/2000/svg">
      <path d="M86 86 L86 10 Q86 4 80 4 L4 4" fill="none" stroke={c1} strokeWidth="2.5"/>
      <path d="M81 80 L81 14 Q81 9 76 9 L10 9" fill="none" stroke={c2} strokeWidth="1.2"/>
      <circle cx="82" cy="8" r="4.5" fill={dot} stroke={c1} strokeWidth="1.2"/>
      <circle cx="82" cy="8" r="2" fill="#fff8c0" opacity=".7"/>
      <path d="M74 4 Q68 10 74 17 Q67 13 60 17 Q65 10 60 4 Q67 8 74 4Z" fill={c1}/>
      <path d="M86 16 Q80 22 73 16 Q77 23 73 30 Q80 25 86 30 Q82 23 86 16Z" fill={c1}/>
      <circle cx="74" cy="16" r="5" fill={c1} stroke={vn} strokeWidth="1.2"/>
      <circle cx="74" cy="16" r="2.5" fill={dot}/>
      <circle cx="64" cy="8" r="3.2" fill={vn}/>
      <circle cx="69" cy="5" r="2" fill={dot} opacity=".85"/>
      <circle cx="82" cy="26" r="3.2" fill={vn}/>
      <circle cx="85" cy="21" r="2" fill={dot} opacity=".85"/>
      <path d="M82 38 Q79 30 74 22" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <path d="M52 8 Q60 11 68 16" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <circle cx="80" cy="40" r="2" fill={dot} opacity=".5"/>
      <circle cx="50" cy="10" r="2" fill={dot} opacity=".5"/>
    </svg>
  )

  /* ── Bottom-left: arms go RIGHT and UP — mirrored on Y axis ── */
  const BL = () => (
    <svg style={{...style, bottom:-5, left:-5}} viewBox="0 0 90 90" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 4 L4 80 Q4 86 10 86 L86 86" fill="none" stroke={c1} strokeWidth="2.5"/>
      <path d="M9 10 L9 76 Q9 81 14 81 L80 81" fill="none" stroke={c2} strokeWidth="1.2"/>
      <circle cx="8" cy="82" r="4.5" fill={dot} stroke={c1} strokeWidth="1.2"/>
      <circle cx="8" cy="82" r="2" fill="#fff8c0" opacity=".7"/>
      <path d="M16 86 Q22 80 16 73 Q23 77 30 73 Q25 80 30 86 Q23 82 16 86Z" fill={c1}/>
      <path d="M4 74 Q10 68 17 74 Q13 67 17 60 Q10 65 4 60 Q8 67 4 74Z" fill={c1}/>
      <circle cx="16" cy="74" r="5" fill={c1} stroke={vn} strokeWidth="1.2"/>
      <circle cx="16" cy="74" r="2.5" fill={dot}/>
      <circle cx="26" cy="82" r="3.2" fill={vn}/>
      <circle cx="21" cy="85" r="2" fill={dot} opacity=".85"/>
      <circle cx="8" cy="64" r="3.2" fill={vn}/>
      <circle cx="5" cy="69" r="2" fill={dot} opacity=".85"/>
      <path d="M8 52 Q11 60 16 68" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <path d="M38 82 Q30 79 22 74" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <circle cx="10" cy="50" r="2" fill={dot} opacity=".5"/>
      <circle cx="40" cy="80" r="2" fill={dot} opacity=".5"/>
    </svg>
  )

  /* ── Bottom-right: arms go LEFT and UP — mirrored on both axes ── */
  const BR = () => (
    <svg style={{...style, bottom:-5, right:-5}} viewBox="0 0 90 90" xmlns="http://www.w3.org/2000/svg">
      <path d="M86 4 L86 80 Q86 86 80 86 L4 86" fill="none" stroke={c1} strokeWidth="2.5"/>
      <path d="M81 10 L81 76 Q81 81 76 81 L10 81" fill="none" stroke={c2} strokeWidth="1.2"/>
      <circle cx="82" cy="82" r="4.5" fill={dot} stroke={c1} strokeWidth="1.2"/>
      <circle cx="82" cy="82" r="2" fill="#fff8c0" opacity=".7"/>
      <path d="M74 86 Q68 80 74 73 Q67 77 60 73 Q65 80 60 86 Q67 82 74 86Z" fill={c1}/>
      <path d="M86 74 Q80 68 73 74 Q77 67 73 60 Q80 65 86 60 Q82 67 86 74Z" fill={c1}/>
      <circle cx="74" cy="74" r="5" fill={c1} stroke={vn} strokeWidth="1.2"/>
      <circle cx="74" cy="74" r="2.5" fill={dot}/>
      <circle cx="64" cy="82" r="3.2" fill={vn}/>
      <circle cx="69" cy="85" r="2" fill={dot} opacity=".85"/>
      <circle cx="82" cy="64" r="3.2" fill={vn}/>
      <circle cx="85" cy="69" r="2" fill={dot} opacity=".85"/>
      <path d="M82 52 Q79 60 74 68" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <path d="M52 82 Q60 79 68 74" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <circle cx="80" cy="50" r="2" fill={dot} opacity=".5"/>
      <circle cx="50" cy="80" r="2" fill={dot} opacity=".5"/>
    </svg>
  )

  return (
    <div style={{ position:"absolute", inset:0, pointerEvents:"none", zIndex:10, overflow:"visible" }} aria-hidden="true">
      <TL/><TR/><BL/><BR/>
    </div>
  )
}

/* ─────────────────────────────────────────────────────────────────────────
   ITEM DETAIL MODAL  — LevelUpNotification card aesthetic
───────────────────────────────────────────────────────────────────────── */
const ItemModal = ({ item, stats, regDoc, onClose }) => {
  const rc     = RARITY_CONFIG[item.rarity]
  const earned = isEarned(item, stats)
  const val    = getStatValue(item, stats)
  const extraDetail = item.detail ? item.detail(regDoc) : null

  useEffect(() => {
    const handler = e => { if (e.key === "Escape") onClose() }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [onClose])

  return (
    <div className="inv-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div
        className="inv-modal"
        style={{ "--rarity-color": rc.color, "--rarity-glow": rc.glow }}
      >
        {/* Scanlines */}
        <div className="inv-modal-scanlines" aria-hidden="true" />

        {/* Ornamental corners */}
        
        
        
        

        <CornerSVG gold />
        {/* Inner inset border */}
        <div className="inv-modal-inset" />

        {/* Close */}
        <button className="inv-modal-close" onClick={onClose} aria-label="Close">✕</button>

        {/* Gold shimmer bar */}
        <div className="inv-modal-topbar" />

        {/* Gold rule */}
        <div className="inv-modal-rule" />

        {/* Icon */}
        <div className={`inv-modal-icon ${earned ? "earned" : "locked"}`}>
          <span>{item.requiresLevel ? "🔒" : item.icon}</span>
          {earned && <div className="inv-modal-icon-ring" />}
        </div>

        {/* Rarity */}
        <div className="inv-modal-rarity">{rc.label}</div>

        {/* Name */}
        <h2 className="inv-modal-name">{item.label}</h2>

        {/* Status pill */}
        <div className={`inv-modal-status ${earned ? "inv-status-earned" : "inv-status-locked"}`}>
          {item.comingSoon ? "⏳ Coming Soon" : earned ? "✦ ACQUIRED" : "🔒 NOT YET EARNED"}
        </div>

        {/* Gold rule */}
        <div className="inv-modal-rule" />

        {/* Description */}
        <p className="inv-modal-desc">{item.description}</p>

        {/* Extra register-based detail */}
        {extraDetail && (
          <p className="inv-modal-extra">{extraDetail}</p>
        )}

        {/* Stat value */}
        {item.statLabel && (
          <div className="inv-modal-stat-row">
            <span className="inv-modal-stat-label">{item.statLabel}</span>
            <span className="inv-modal-stat-val">
              {item.requiresLevel
                ? "Lvl 100 Required"
                : earned && val !== null
                  ? val.toLocaleString()
                  : "—"}
            </span>
          </div>
        )}

        {/* Flavor */}
        <p className="inv-modal-flavor">"{item.flavor}"</p>

        {/* Bottom rule */}
        <div className="inv-modal-rule" />
      </div>
    </div>
  )
}

/* ─────────────────────────────────────────────────────────────────────────
   ITEM SLOT
───────────────────────────────────────────────────────────────────────── */
const ItemSlot = ({ item, stats, index, onSelect }) => {
  const rc     = RARITY_CONFIG[item.rarity]
  const earned = isEarned(item, stats)
  const locked = item.requiresLevel || item.comingSoon

  return (
    <button
      className={`inv-slot ${earned ? "inv-slot--earned" : "inv-slot--locked"} ${locked ? "inv-slot--prestige" : ""}`}
      style={{ "--rarity-color": rc.color, "--rarity-glow": rc.glow, "--stagger": index }}
      onClick={() => onSelect(item)}
      title={item.label}
      aria-label={`${item.label} — ${earned ? "Acquired" : "Locked"}`}
    >
      {earned && <div className="inv-slot-glow" aria-hidden="true" />}
      <div className="inv-slot-strip" />
      <div className="inv-slot-icon">{locked ? "🔒" : item.icon}</div>
      <div className="inv-slot-label">{item.label}</div>
      {earned && <div className="inv-slot-check">✦</div>}
      {item.rarity === "mythic" && (
        <>
          <div className="inv-slot-spark inv-slot-spark-1" aria-hidden="true" />
          <div className="inv-slot-spark inv-slot-spark-2" aria-hidden="true" />
          <div className="inv-slot-spark inv-slot-spark-3" aria-hidden="true" />
        </>
      )}
    </button>
  )
}

/* ─────────────────────────────────────────────────────────────────────────
   STATUS PANEL  — LevelUpNotification golden card + real DB data
───────────────────────────────────────────────────────────────────────── */
const StatusPanel = ({ lbEntry, regDoc }) => {
  const stats = buildStats(lbEntry, regDoc)

  const firstName = regDoc?.Firstname || ""
  const lastName  = regDoc?.Lastname  || ""
  const name      = [firstName, lastName].filter(Boolean).join(" ") || "Adventurer"
  const email     = regDoc?.EmailAddress || ""
  const badge     = regDoc?.badgeType    || regDoc?.Ticket_Variant || "Delegate"

  const totalScore    = stats.totalScore    || 0
  const checkIns      = stats.checkIns      || 0
  const boothVisits   = stats.boothVisits   || 0
  const sessBooked    = stats.sessBooked    || 0

  // Level: every 50 pts = 1 level
  const level = Math.max(1, Math.min(99, Math.floor(totalScore / 50) + 1))

  // Title from score
  const title = totalScore >= 500 ? "Converge Legend"
              : totalScore >= 300 ? "Retail Visionary"
              : totalScore >= 150 ? "Tech Explorer"
              : totalScore >= 50  ? "Curious Learner"
              : "Apprentice"

  // RPG stats from real data
  const str = Math.min(99, 10 + boothVisits * 6)
  const agi = Math.min(99, 10 + checkIns    * 5)
  const vit = Math.min(99, 10 + Math.floor(totalScore / 20))
  const int = Math.min(99, 10 + (stats.quizScore ?? 0))
  const per = Math.min(99, 10 + Math.floor(((stats.feedbackScore ?? 0) + (stats.networkScore ?? 0)) / 2))
  const hp  = 100 + totalScore * 2
  const mp  = 50  + checkIns   * 20

  return (
    <div className="inv-status">
      {/* Scanlines */}
      <div className="inv-status-scanlines" aria-hidden="true" />

      {/* Ornamental corners — gold themed */}
      
      
      
      

      <CornerSVG gold />
      {/* Inner inset */}
      <div className="inv-status-inset" />

      {/* Top shimmer bar */}
      <div className="inv-status-shimmer" />

      {/* Title row */}
      <div className="inv-status-titlerow">
        <div className="inv-status-rule" />
        <span className="inv-status-title">STATUS</span>
        <div className="inv-status-rule" />
      </div>

      {/* Level + identity */}
      <div className="inv-status-hero">
        <div className="inv-status-level-wrap">
          <div className="inv-status-level-num">{level}</div>
          <div className="inv-status-level-lbl">LEVEL</div>
        </div>
        <div className="inv-status-info">
          <div className="inv-status-name">{name}</div>
          <div className="inv-status-email">{email}</div>
          <div className="inv-status-badge">
            <span className="inv-status-badge-dot" />
            {badge}
          </div>
          <div className="inv-status-titletag">
            <span className="inv-tag-key">TITLE:</span>
            <span className="inv-tag-val">{title}</span>
          </div>
        </div>
      </div>

      {/* Gold divider */}
      <div className="inv-status-divider" />

      {/* HP / MP bars */}
      <div className="inv-status-bars">
        <div className="inv-bar-row">
          <span className="inv-bar-icon inv-bar-icon--hp">♥</span>
          <span className="inv-bar-lbl">HP</span>
          <div className="inv-bar-track">
            <div className="inv-bar-fill inv-bar-hp" style={{ width: `${Math.min(100, (hp / 500) * 100)}%` }} />
          </div>
          <span className="inv-bar-val">{hp}</span>
        </div>
        <div className="inv-bar-row">
          <span className="inv-bar-icon inv-bar-icon--mp">✦</span>
          <span className="inv-bar-lbl">MP</span>
          <div className="inv-bar-track">
            <div className="inv-bar-fill inv-bar-mp" style={{ width: `${Math.min(100, (mp / 300) * 100)}%` }} />
          </div>
          <span className="inv-bar-val">{mp}</span>
        </div>
      </div>

      {/* Stats grid */}
      <div className="inv-status-stats">
        {[
          { icon: "⚔",  key: "STR", val: str,  sub: `${boothVisits} booths` },
          { icon: "💜", key: "VIT", val: vit,  sub: `${totalScore} pts` },
          { icon: "🌙", key: "AGI", val: agi,  sub: `${checkIns} check-ins` },
          { icon: "🔮", key: "INT", val: int,  sub: stats.quizScore !== null ? `quiz: ${stats.quizScore}` : "no quiz yet" },
        ].map(s => (
          <div key={s.key} className="inv-stat-item">
            <span className="inv-stat-icon">{s.icon}</span>
            <span className="inv-stat-key">{s.key}</span>
            <span className="inv-stat-val">{s.val}</span>
            <span className="inv-stat-sub">{s.sub}</span>
          </div>
        ))}
        <div className="inv-stat-item inv-stat-wide">
          <span className="inv-stat-icon">👁</span>
          <span className="inv-stat-key">PER</span>
          <span className="inv-stat-val">{per}</span>
          <span className="inv-stat-sub">Pts: {totalScore}</span>
        </div>
      </div>

      {/* Session detail */}
      <div className="inv-status-sessions">
        <div className="inv-sessions-label">SESSIONS BOOKED</div>
        <div className="inv-sessions-list">
          {(regDoc?.sessionsBooked || []).map((s, i) => (
            <span key={i} className="inv-session-pill">{s}</span>
          ))}
          {(!regDoc?.sessionsBooked || regDoc.sessionsBooked.length === 0) && (
            <span className="inv-session-none">None booked yet</span>
          )}
        </div>
      </div>

      {/* Bottom rule */}
      <div className="inv-status-divider" />
    </div>
  )
}

/* ─────────────────────────────────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────────────────────────────────── */
const Inventory = () => {
  const [lbEntry,  setLbEntry]  = useState(null)
  const [regDoc,   setRegDoc]   = useState(null)
  const [loading,  setLoading]  = useState(true)
  const [selected, setSelected] = useState(null)
  const [filter,   setFilter]   = useState("all")

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}")
  const email    = authUser?.EmailAddress || authUser?.email || ""
  const API      = process.env.REACT_APP_ADMIN || ""

  useEffect(() => {
    if (!email || !API) { setLoading(false); return }

    Promise.all([
      // Leaderboard
      fetch(`${API}/getscores`)
        .then(r => r.json())
        .then(all => Array.isArray(all)
          ? all.find(u => (u.id || "").split("|")[0].toLowerCase() === email.toLowerCase())
          : null
        ).catch(() => null),
      // Register — use authUser from localStorage (already fetched at login)
      Promise.resolve(authUser),
    ]).then(([lb, reg]) => {
      setLbEntry(lb || null)
      setRegDoc(reg  || null)
    }).finally(() => setLoading(false))
  }, [email, API]) // eslint-disable-line

  const stats = buildStats(lbEntry, regDoc)

  const handleSelect = useCallback(item => setSelected(item), [])
  const handleClose  = useCallback(() => setSelected(null),   [])

  const FILTERS = [
    { key: "all",     label: "All" },
    { key: "earned",  label: "Acquired" },
    { key: "locked",  label: "Locked" },
  ]

  const visible = INVENTORY_ITEMS.filter(item => {
    if (filter === "earned") return  isEarned(item, stats)
    if (filter === "locked") return !isEarned(item, stats)
    return true
  })

  const earnedCount = INVENTORY_ITEMS.filter(i => isEarned(i, stats)).length

  if (loading) {
    return (
      <div className="inv-page">
        <div className="inv-loader">
          <div className="inv-loader-ring" />
          <p>Loading inventory…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="inv-page">
      {/* Animated background (leaderboard style) */}
      <div className="inv-bg-orb inv-bg-orb-1" aria-hidden="true" />
      <div className="inv-bg-orb inv-bg-orb-2" aria-hidden="true" />
      <div className="inv-bg-orb inv-bg-orb-3" aria-hidden="true" />
      <div className="inv-bg-grid"              aria-hidden="true" />
      <div className="inv-bg-scanlines"         aria-hidden="true" />
      <div className="inv-shoot"                aria-hidden="true">
        <span/><span/><span/><span/><span/>
        <span/><span/><span/><span/>
        <i/><b/><em/>
      </div>

      <div className="inv-content">
        {/* Header */}
        <header className="inv-header">
          <div className="inv-header-line inv-header-line--gold" />
          <h1 className="inv-header-title">
            <span className="inv-header-bracket">[</span>
            INVENTORY
            <span className="inv-header-bracket">]</span>
          </h1>
          <div className="inv-header-line inv-header-line--gold" />
        </header>

        <div className="inv-layout">
          {/* STATUS — left */}
          <aside className="inv-left">
            <StatusPanel lbEntry={lbEntry} regDoc={regDoc} />
          </aside>

          {/* ITEMS — right */}
          <main className="inv-right">
            {/* Filters */}
            <div className="inv-filters">
              {FILTERS.map(f => (
                <button
                  key={f.key}
                  className={`inv-filter-btn ${filter === f.key ? "active" : ""}`}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
              <div className="inv-filter-count">
                {earnedCount} / {INVENTORY_ITEMS.length} acquired
              </div>
            </div>

            {/* Grid */}
            <div className="inv-grid">
              {visible.map((item, i) => (
                <ItemSlot key={item.id} item={item} stats={stats} index={i} onSelect={handleSelect} />
              ))}
              {visible.length === 0 && (
                <div className="inv-empty">
                  <span>◎</span>
                  <p>No items in this category.</p>
                </div>
              )}
            </div>

            {/* Legend */}
            <div className="inv-legend">
              {Object.entries(RARITY_CONFIG).map(([key, cfg]) => (
                <div key={key} className="inv-legend-item">
                  <div className="inv-legend-dot" style={{ background: cfg.color, boxShadow: `0 0 6px ${cfg.glow}` }} />
                  <span>{cfg.label}</span>
                </div>
              ))}
            </div>
          </main>
        </div>
      </div>

      {/* Modal */}
      {selected && (
        <ItemModal
          item={selected}
          stats={stats}
          regDoc={regDoc}
          onClose={handleClose}
        />
      )}
    </div>
  )
}

export default Inventory